import * as React from 'react';

import type { BlockImageData, InlineImageData } from '@gitbook/doc-core';
import { createPalette, usePaletteHandle } from '@gitbook/react-uikit';
import type { Block, Inline } from '@gitbook/slate';
import type { EditorContainer } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import { Button, type ButtonProps, type ButtonSize } from '@gitbook/spine-react';

import { useInsertFiles } from '../../InsertFilesDialog';
import { ContentEditorContext } from '../contexts';

/**
 * Button to replace the source of an image (block or inline).
 */
export function ReplaceImageButton(props: {
    editor: EditorContainer;
    node: Block | Inline;
    className?: string;
    size?: ButtonSize;
    kind?: ButtonProps['kind'];
    label?: ButtonProps['label'];
    leadingIcon?: ButtonProps['leadingIcon'];
    isBroken?: boolean;
}): React.ReactElement | null {
    const { editor, node, className, size = 'medium', kind = 'blank', label } = props;

    const buttonRef = React.useRef<HTMLButtonElement>(null);
    const paletteHandle = usePaletteHandle(buttonRef);
    const insertFiles = useInsertFiles();
    const {
        blocks: { withImages },
    } = React.useContext(ContentEditorContext);

    if (!withImages) {
        return null;
    }

    const replaceWith = async (propName: 'ref' | 'refDark', title: string, resetDark = false) => {
        const [file] = await insertFiles.prompt({
            title,
            accept: 'images',
            acceptURL: true,
        });

        if (!file) {
            return;
        }

        editor.change((change) => {
            change.insertDataByKey(node.key, [propName], file);

            if (resetDark) {
                change.removeDataByKey(node.key, ['refDark']);
            }

            if (node.object === 'block') {
                // Reset block sizing
                change.removeDataPathByKey(node.key, ['height']);
                change.removeDataPathByKey(node.key, ['width']);
            }
        });
    };

    const onPress = () => {
        // If the image is broken we open the dialog to replace the image directly
        if (props.isBroken) {
            replaceWith('ref', 'Replace image', true);
            return;
        }

        // We need to refocus the editor to prevent the toolbar on inline images
        // to disappear and close this palette.
        editor.change((change) => change.focus());

        const imageData = node.data.toFrozenObject() as InlineImageData | BlockImageData;

        paletteHandle.open(
            createPalette({
                groups: [
                    {
                        key: 'replace',
                        commands: [
                            {
                                key: 'all',
                                icon: Icon.Replace,
                                title: 'Replace image',
                                subtitle: 'For both light and dark modes',
                                trigger: () => {
                                    replaceWith('ref', 'Replace image', true);
                                },
                            },
                        ],
                    },
                    {
                        key: 'byTheme',
                        commands: imageData.refDark
                            ? [
                                  {
                                      key: 'light',
                                      icon: Icon.ThemeLight,
                                      title: 'For light mode',
                                      trigger: () => {
                                          replaceWith('ref', 'Replace image for light mode');
                                      },
                                  },
                                  {
                                      key: 'dark',
                                      icon: Icon.ThemeDark,
                                      title: 'For dark mode',
                                      trigger: () => {
                                          replaceWith('refDark', 'Replace image for dark mode');
                                      },
                                  },
                              ]
                            : [
                                  {
                                      key: 'dark',
                                      icon: Icon.ThemeDark,
                                      title: 'Add image for Dark mode',
                                      trigger: () => {
                                          replaceWith('refDark', 'Add image for dark mode');
                                      },
                                  },
                              ],
                    },
                ],
            })
        );
    };

    return (
        <Button
            ref={buttonRef}
            active={paletteHandle.opened}
            size={size}
            label={label ?? 'Replace image'}
            kind={kind}
            leadingIcon={props.leadingIcon ?? Icon.Replace}
            onPress={onPress}
            className={className}
            iconOnly={!label}
        />
    );
}
